<template>
  <v-app :dark="isDarkTheme" :style="organizationThemeStyle">
    <error-view v-if="isOrganizationNotFound"/>
    <div v-else-if="isLoadingOrganization || !organization" class="as-org-loading">
      <v-container fill-height>
        <v-row align="center" justify="center">
          <v-col cols="12" class="text-center">
            <loading-in-progress/>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <div v-else>
      <v-app-bar
        app
        :dark="isDarkTheme"
        class="as-header as-organization-page"
        dense
        absolute
        height="44px"
      >
        <v-toolbar-title>
          <router-link 
            link
            :to="organizationUrl"
          >
            <v-avatar v-if="organization.src_logo_small" size="35px" class="mr-2">
              <v-img :src='organization.src_logo_small' :alt="organization.name"></v-img>
            </v-avatar>
            {{ organization.name }}
          </router-link>
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <!-- Social Media Links and Account Button -->
        <div class="social-media-header">
          <!-- Show only first social media link -->
          <a 
            v-if="socialMediaLinks.length > 0"
            @click="openSocialLink(socialMediaLinks[0])"
            class="social-media-header-link"
            :title="getPlatformDisplayName(socialMediaLinks[0].type)"
          >
            <v-icon 
              class="social-media-header-icon"
            >
              {{ getPlatformIcon(socialMediaLinks[0].type) }}
            </v-icon>
          </a>
          
          <user-account-menu
            variant="light"
          />
        </div>
      </v-app-bar>

      <v-main>
        <!-- Full-bleed and above the page content: this app bar is `absolute`, so it scrolls
             away and the notice can stick to the top of the viewport on its own. -->
        <timezone-notice-banner/>
        <router-view :key="$route.fullPath"/>
      </v-main>
      
      <OrganizationFooter/>
    </div>
  </v-app>
</template>

<script>
import OrganizationFooter from '@/components/public/layout/OrganizationFooter.vue'
import LoadingInProgress from '@/components/public/LoadingInProgress.vue'
import UserAccountMenu from '@/components/private/layout/UserAccountMenu.vue'
import TimezoneNoticeBanner from '@/components/TimezoneNoticeBanner.vue'
import ErrorView from '@/views/ErrorView.vue'
import { getPlatformIcon, getPlatformDisplayName } from '@/js/utils/socialMediaUtil'
import { isSearchIndexingEnabled, ROBOTS_NOINDEX } from '@/js/utils/searchIndexing.js'

import OrganizationRepository from '@/js/api/OrganizationRepository'
import urlUtil from '@/js/utils/urlUtil'
import ClassRepository from '@/js/api/ClassRepository'

export default {
  components: { OrganizationFooter, LoadingInProgress, UserAccountMenu, TimezoneNoticeBanner, ErrorView },
  
  metaInfo () {
    return {
      title: this.organization ? this.organization.name : "ASport",
      titleTemplate: this.$t('seo.organization-title-template', { organization: this.organization ? this.organization.name : "" }),
      // One place for every page of the studio [F32]. Not-found pages get theirs from ErrorView.
      meta: this.organization && !isSearchIndexingEnabled(this.organization)
        ? [{ name: 'robots', content: ROBOTS_NOINDEX }]
        : []
    }
  },
  data: () => ({
    isLoadingOrganization: true,
    /** The studio does not exist or is closed: every page under it shows not-found (noindex) at its own URL [B31]. */
    isOrganizationNotFound: false
  }),
  methods: {
    getPlatformIcon,
    getPlatformDisplayName,
    openSocialLink(link) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    },
    async sharePage() {
      const shareData = {
        title: this.organization?.name || 'ASport',
        text: this.organization?.description ? this.organization.description.replace(/<[^>]*>/g, '').substring(0, 100) + '...' : this.$t('organization.share.fallback-text', { organization: this.organization?.name }),
        url: window.location.href
      };
      
      try {
        if (navigator.share && navigator.share.canShare(shareData)) {
          await navigator.share(shareData);
        } else {
          // Fallback: copy link to clipboard
          await navigator.clipboard.writeText(window.location.href);
          this.$toast.success(this.$t('organization.share.success'));
        }
      } catch (error) {
        // Fallback: copy link to clipboard
        try {
          await navigator.clipboard.writeText(window.location.href);
          this.$toast.success(this.$t('organization.share.success'));
        } catch (clipboardError) {
          // Final fallback: show URL in alert
          this.$toast.error(this.$t('organization.share.error', { url: window.location.href }));
        }
      }
    },
    async loadOrganizationAndClasses (orgIdentifier) {
      const currentIdentifier = this.$store.getters['organization/organization']?.identifier
      if (currentIdentifier !== orgIdentifier) {
        this.$store.dispatch('organization/resetState')
        this.isLoadingOrganization = true
      }
      this.isOrganizationNotFound = false

      try {
        const organization = await OrganizationRepository.findByIdentifier(orgIdentifier)
        this.$store.dispatch('organization/setOrganization', organization)
        if (!organization) {
          this.isOrganizationNotFound = true
          this.isLoadingOrganization = false
          return
        }
        // Warm the public membership templates in the store so class pages can show their
        // "memberships from X" hint without their own request and the memberships page loads fast.
        this.$store.dispatch('organization/loadMembershipTemplates').catch(() => { /* best-effort */ })
        const myClasses = await ClassRepository.getByOrganization(organization.id)
        this.$store.dispatch('organization/setClasses', myClasses)
        this.isLoadingOrganization = false
      } catch (error) {
        console.error('Error loading organization:', error)
        this.isLoadingOrganization = false
      }
    }
  },
  watch: {
    '$route.params.identifier': {
      handler (newId) {
        if (newId != null) {
          this.loadOrganizationAndClasses(newId)
        }
      },
      immediate: true
    }
  },
  computed: {
    organization: function() {
      return this.$store.getters['organization/organization']
    },
    isLoggedIn: function() {
      return this.$store.getters['user/isLoggedIn']
    },
    userName: function() {
      return this.$store.getters['user/name'] || this.$store.getters['user/user']?.name || ''
    },
    user: function() {
      return this.$store.getters['user/user']
    },
    isAccountTypeOrganization: function() {
      return this.$store.getters['dashboard/isAccountTypeOrganization']
    },
    isAccountTypeParticipant: function() {
      return this.$store.getters['dashboard/isAccountTypeParticipant']
    },
    accountButtonUrl: function() {
      if (this.isLoggedIn) {
        // Trainer: redirect to events (dashboard not ready yet)
        if (this.isAccountTypeOrganization) {
          return { path: urlUtil.getLocalisedMyPagesUrl('events') }
        }
        // Participant: redirect to participant dashboard
        if (this.isAccountTypeParticipant) {
          return { path: urlUtil.getLocalisedMyPagesUrl('participant-dashboard') }
        }
        // Fallback: redirect to account (should not happen normally)
        return { path: urlUtil.getLocalisedMyPagesUrl('account') }
      }
      // Use organization login route if we're in organization context
      if (this.organization && this.organization.identifier) {
        return { 
          path: urlUtil.getLocalisedOrganizationUrl(this.organization, 'login'), 
          query: { returnUrl: this.$route.fullPath } 
        }
      }
      return { 
        path: urlUtil.getLocalisedUrl('/login'), 
        query: { returnUrl: this.$route.fullPath } 
      }
    },
    organizationUrl: function() {
      if (this.organization){
        return urlUtil.getLocalisedOrganizationUrl(this.organization)
      }
      return null
    },
    isMainPage: function() {
      if (this.organization){
        return urlUtil.getLocalisedOrganizationUrl(this.organization, null) === this.$route.fullPath
      }
      return false
    },
    organizationPrimaryColor () {
      if (!this.organization)
        return ""
      
      if (this.organization && this.organization.frontend_settings && this.organization.frontend_settings.color_primary && this.organization.frontend_settings.color_primary.match(/^#[0-9A-F]{6}$/i)){
          return this.organization.frontend_settings.color_primary
      }
      return '#ffa500'
    },
    organizationSecondaryColor () {
      if (!this.organization)
        return ""

      if (this.organization && this.organization.frontend_settings && this.organization.frontend_settings.color_secondary && this.organization.frontend_settings.color_secondary.match(/^#[0-9A-F]{6}$/i)){
          return this.organization.frontend_settings.color_secondary
      }
      //Else let's use the primary color and if it is not set then it will have default value already set
      return this.organizationPrimaryColor
    },
    organizationTheme () {
      return this.organization?.frontend_settings?.theme || 'light'
    },
    isDarkTheme() {
      return this.organizationTheme === 'dark'
    },
    socialMediaLinks () {
      let mediaLinks = this.organization?.social_media_settings?.media_links || []
      mediaLinks = mediaLinks.map(link => ({
        url: urlUtil.formatUrl(link.url),
        type: link.type
      }))
      return mediaLinks
    },
    organizationThemeStyle() {
      const primaryColor = this.organizationPrimaryColor
      const secondaryColor = this.organizationSecondaryColor
      
      return `--organization-color-primary: ${primaryColor}; --organization-color-secondary: ${secondaryColor}; --organization-theme-${this.organizationTheme}`
    }
  }
}
/**var(--color-pallet-1) !important; */
</script>
<style lang="scss">
.as-org-loading {
  min-height: 100vh;
}
.v-application.as-page .as-organization-page .home-icon a {
  text-decoration: none;
  font-size: 16px;
}
.v-application.as-page .as-organization-page.as-header a {
  text-decoration: none;
}
.v-application.as-page .as-organization-page.as-header a {
  color: var(--organization-color-secondary) !important;
  font-weight: 450;
  font-size: 16px
}
.v-application.as-page .as-organization-style {
  color: var(--organization-color-secondary) !important;
}

/* Header Social Media Links */
.social-media-header {
  display: flex;
  gap: 12px;
  align-items: center;
}

.social-media-header-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  text-decoration: none;
  cursor: pointer;
}

.social-media-header-link:hover {
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.social-media-header-link:hover .social-media-header-icon {
  color: var(--organization-color-secondary) !important;
  filter: brightness(0.85);
}

.social-media-header-icon {
  color: var(--organization-color-secondary) !important;
  transition: filter 0.2s ease;
}

/* Account Button with Icon and Text */
.account-button-link {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  text-decoration: none;
  cursor: pointer;
  color: var(--organization-color-secondary) !important;
  font-weight: 450;
  font-size: 14px;
}

.account-button-link:hover {
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.account-button-link:hover .account-button-icon,
.account-button-link:hover .account-button-text {
  color: var(--organization-color-secondary) !important;
  filter: brightness(0.85);
}

.account-button-icon {
  color: var(--organization-color-secondary) !important;
  flex-shrink: 0;
  transition: filter 0.2s ease;
}

.account-button-text {
  white-space: nowrap;
  color: var(--organization-color-secondary) !important;
  transition: filter 0.2s ease;
}

.account-button-chevron {
  margin-left: 2px;
  flex-shrink: 0;
}

/* Mobile responsive for header social media */
@media (max-width: 768px) {
  .social-media-header {
    gap: 10px;
  }
  
  .social-media-header-link {
    width: 28px;
    height: 28px;
  }
  
  .account-button-link {
    padding: 6px 10px;
    gap: 6px;
    font-size: 12px;
  }
  
  .account-button-icon {
    font-size: 18px !important;
  }
}
</style>
